import { getItemDefinition } from "@endclock/shared/items/catalog";
import type { EquipmentVisualState, LightDeviceDefinition } from "@endclock/shared";
import type { Mat4 } from "../engine/math.js";
import type { LocalLight } from "../engine/lighting.js";
import type { CharacterAsset } from "./character-asset.js";

export const DEFAULT_EQUIPMENT_VISUAL: EquipmentVisualState = Object.freeze({ garments: { LEGS: "jeans_basic" }, mainHand: null });
export const HELD_ITEM_VISUALS: Readonly<Record<string, string>> = Object.freeze({ "tool.flashlight_aa_basic": "flashlight_aa_basic" });
// Authored in the same bind space as the mitten; the actual skeleton has no wrist.
export const FLASHLIGHT_SOCKET = Object.freeze({ joint: "lower_arm_r", position: [0.242, 0.578, 0.035] as const, direction: [0, -1, 0] as const });

export function equipmentMeshGroups(state: EquipmentVisualState): Set<string> {
  const groups = new Set<string>(["body", ...Object.values(state.garments)]);
  const held = state.mainHand && HELD_ITEM_VISUALS[state.mainHand.definitionId];
  if (held) groups.add(held);
  return groups;
}

/** Identical skin palette/model multiplication to the attachment's vertex shader. */
export function flashlightTransform(asset: CharacterAsset, palette: Float32Array, model: Mat4): { position: [number, number, number]; direction: [number, number, number] } | null {
  const joint = asset.joints.findIndex((entry) => entry.name === FLASHLIGHT_SOCKET.joint);
  if (joint < 0) return null;
  const point = transform(palette, joint * 16, FLASHLIGHT_SOCKET.position, 1);
  const axis = transform(palette, joint * 16, FLASHLIGHT_SOCKET.direction, 0);
  const position = transform(model, 0, point, 1), direction = transform(model, 0, axis, 0);
  const length = Math.hypot(...direction);
  if (length < 0.0001 || ![...position, ...direction].every(Number.isFinite)) return null;
  return { position, direction: [direction[0] / length, direction[1] / length, direction[2] / length] };
}

export function heldFlashlightLight(id: string, state: EquipmentVisualState, asset: CharacterAsset, palette: Float32Array, model: Mat4): LocalLight | null {
  const hand = state.mainHand;
  const device = hand && getItemDefinition(hand.definitionId)?.lightDevice;
  if (!hand?.lightOn || !device) return null;
  const attachment = flashlightTransform(asset, palette, model);
  return attachment ? { id: `held:${id}`, type: "spot", ...attachment, ...flashlightPhotometry(device) } : null;
}

/** Fixed focus participates in the engine's existing bounded, distance-falloff lighting. */
export function flashlightPhotometry(device: LightDeviceDefinition): Pick<LocalLight, "color" | "intensity" | "radius" | "innerCone" | "outerCone" | "distanceFalloff" | "photometric"> {
  const outerCone = Math.max(0.02, Math.min(Math.PI / 2, device.beamAngleDegrees * Math.PI / 360));
  const solidAngle = 2 * Math.PI * (1 - Math.cos(outerCone));
  const temperature = Math.max(0, Math.min(1, (device.colorTemperatureKelvin - 2700) / 3800));
  return {
    distanceFalloff: 0.01, photometric: true,
    color: [1 - temperature * 0.09, 0.88 + temperature * 0.10, 0.67 + temperature * 0.33],
    intensity: Math.max(0.1, Math.min(256, device.luminousFluxLumens / (solidAngle * 3.2))),
    radius: Math.max(1, Math.min(64, device.effectiveRangeMeters)), outerCone,
    innerCone: outerCone * (1 - Math.max(0.02, Math.min(0.95, device.beamSoftness))),
  };
}
function transform(matrix: Float32Array, offset: number, vector: readonly number[], w: number): [number, number, number] {
  return [0, 1, 2].map((row) => matrix[offset + row]! * vector[0]! + matrix[offset + row + 4]! * vector[1]!
    + matrix[offset + row + 8]! * vector[2]! + matrix[offset + row + 12]! * w) as [number, number, number];
}
